'use client';
import { ReactNode, useState } from 'react';
import cn from 'classnames';

import { useFormikContext } from 'formik';
import Text from '@/components/common/Text/Text';
import { useGentleBlurField } from '@/components/form/lib/useGentleBlurField';

import { isValidEmail } from '@/util/regexp';

import Mailcheck from 'mailcheck';

import '../InputField/InputField.style.scss';
import './EmailField.style.scss';
import Link from '@/components/common/Link/Link';
import { useModal } from '@/components/hook/modal';
import LoginModal from '@/components/modal/LoginModal/LoginModal';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useRouter } from 'next/navigation';

type EmailFieldProps = {
  className?: string;
  label?: ReactNode;
  name: string;
  placeholder?: string;
  onFocus?: () => void;
  /** Optional HTML autocomplete token (e.g. "email"). */
  autoComplete?: string;
  'data-testid'?: string;
};

function emailSuggestionResponse(email: string) {
  return Mailcheck.run({
    email,
    suggested: async (suggestion: { full: any }) => {
      return `Did you mean ${suggestion.full}?`;
    }
  });
}

function EmailField(props: EmailFieldProps) {
  const {
    className = '',
    name,
    label,
    placeholder,
    onFocus,
    autoComplete,
    'data-testid': dataTestId
  } = props;
  const [emailWarning, setEmailWarning] = useState<string>('');
  const [field, meta] = useGentleBlurField({ name });
  const { isValidating } = useFormikContext();
  const modalTools = useModal();
  const { isIOSWebView } = useContextBroker();
  const router = useRouter();

  const handleBlurEvent = (e: any) => {
    field.onBlur(e);

    // The latest touched and errors aren't available until after the
    // formik's onBlur handler has been called. Setting a timeout allows us
    // to access the proper touched and errors after onBlur event has
    // occurred.
    setEmailWarning('');

    if (isValidEmail(field.value)) {
      const emailSuggestion = emailSuggestionResponse(field.value);

      if (emailSuggestion as any as boolean) {
        setEmailWarning(emailSuggestion as any as string);
      }
    }
  };

  /**
   * Formik's async validate (the email-exists lookup) doesn't resolve
   * instantly, so right after blur `meta.error` can briefly still hold
   * whatever it was *before* this blur — a stale "required"/"invalid" from
   * an earlier validation pass — until the promise settles. Holding both the
   * red underline and the message back while `isValidating` is true skips
   * that flash instead of showing it and then swapping to the real answer a
   * beat later. The underline used to only check `meta.touched && meta.error`
   * — missing this same guard — so it flashed red on every keystroke-driven
   * validation even though the message beside it correctly stayed hidden.
   */
  const hasFieldError = !isValidating && meta.touched && !!meta.error;
  const showErrorMsg = hasFieldError || (!isValidating && !!emailWarning);
  const errorMessage = (meta.error || emailWarning) as string;
  const reactivateMatch = /welcome back/i;
  const showReactivateLink = reactivateMatch.test(errorMessage);

  const handleReactivate = (e: any) => {
    e.preventDefault();
    modalTools.showModal(
      <LoginModal
        headline='Login to reactivate your account'
        initialValues={{
          username: field.value ?? '',
          password: ''
        }}
        onClose={modalTools.closeModal}
        onSubmitSuccess={() => {
          modalTools.closeModal();
          if (isIOSWebView) {
            router.push('/myaccount/reactivate');
            return;
          }
          router.push('/account/reactivate');
        }}
      />,
      { topClassName: 'bg-gold' }
    );
  };

  return (
    <div className={cn(className, 'InputField')}>
      <label className='InputField-label'>{label}</label>
      <input
        className={cn('InputField-input', { Error: hasFieldError })}
        placeholder={placeholder}
        {...field}
        type='email'
        autoComplete={autoComplete}
        onBlur={handleBlurEvent}
        onFocus={onFocus}
        data-testid={dataTestId}
      />

      {showErrorMsg && (
        <div className='InputField-error'>
          <Text className='InputField-error-text'>
            {showReactivateLink ? (
              <>
                {errorMessage}{' '}
                <Link
                  className='EmailField-reactivateLink EmailField-reactivateLink--mobile'
                  variant='link-xs'
                  href='#'
                  underline
                  onClick={handleReactivate}
                >
                  Reactivate your account instead
                </Link>
                <Link
                  className='EmailField-reactivateLink EmailField-reactivateLink--desktop'
                  variant='link-m'
                  href='#'
                  underline
                  onClick={handleReactivate}
                >
                  Reactivate your account instead
                </Link>
              </>
            ) : (
              <>{errorMessage}</>
            )}
          </Text>
        </div>
      )}
    </div>
  );
}

export default EmailField;
